import { useState, useEffect } from 'react'
import { Save, RefreshCw, AlertCircle, CheckCircle, Clock, XCircle, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'react-hot-toast'

interface CancellationRule {
  id: string
  name: string
  description: string
  timeLimitMinutes: number
  penaltyPercent: number
  isActive: boolean
}

const CancellationPolicyPage = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rules, setRules] = useState<CancellationRule[]>([
    {
      id: 'immediate',
      name: 'Annulation immédiate',
      description: 'Annulation dans les 15 minutes suivant la commande',
      timeLimitMinutes: 15,
      penaltyPercent: 0,
      isActive: true
    },
    {
      id: 'standard',
      name: 'Annulation standard',
      description: 'Annulation jusqu\'à 2 heures avant le retrait',
      timeLimitMinutes: 120,
      penaltyPercent: 0,
      isActive: true
    },
    {
      id: 'late',
      name: 'Annulation tardive',
      description: 'Annulation moins de 2 heures avant le retrait',
      timeLimitMinutes: 0,
      penaltyPercent: 50,
      isActive: true
    }
  ])
  const [globalSettings, setGlobalSettings] = useState({
    allowCancellation: true,
    cancellationWindowMinutes: 120,
    defaultPenaltyPercent: 0,
    requireReason: true,
    notifyMerchant: true,
    autoRefund: true
  })

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true)
        // In a real implementation, fetch from API
        // const response = await fetch('/api/admin/cancellation-policy')
        // const data = await response.json()
        // setRules(data.rules)
        // setGlobalSettings(data.globalSettings)
        await new Promise(resolve => setTimeout(resolve, 500))
      } catch (err) {
        setError('Erreur lors du chargement de la politique d\'annulation')
      } finally {
        setLoading(false)
      }
    }
    loadSettings()
  }, [])

  const handleRuleToggle = (ruleId: string) => {
    setRules(prev =>
      prev.map(rule =>
        rule.id === ruleId ? { ...rule, isActive: !rule.isActive } : rule
      )
    )
  }

  const handleRuleChange = (ruleId: string, field: keyof CancellationRule, value: any) => {
    setRules(prev =>
      prev.map(rule =>
        rule.id === ruleId ? { ...rule, [field]: value } : rule
      )
    )
  }

  const handleGlobalChange = (field: keyof typeof globalSettings, value: any) => {
    setGlobalSettings(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      // In a real implementation, save to API
      // await fetch('/api/admin/cancellation-policy', {
      //   method: 'PUT',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ rules, globalSettings })
      // })
      await new Promise(resolve => setTimeout(resolve, 800))
      toast.success('Politique d\'annulation mise à jour avec succès')
    } catch {
      toast.error('Erreur lors de la sauvegarde de la politique d\'annulation')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48 mt-2" />
          </div>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Politique d'annulation
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gérez les règles et conditions d'annulation des commandes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setLoading(true)
              setTimeout(() => setLoading(false), 500)
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5 text-gray-500" />
            Paramètres généraux
          </CardTitle>
          <CardDescription>
            Configuration globale de la politique d'annulation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-700">Autoriser les annulations</p>
                <p className="text-xs text-gray-500">Permettre aux clients d'annuler leurs commandes</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettings.allowCancellation}
                  onChange={(e) => handleGlobalChange('allowCancellation', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fenêtre d'annulation (minutes)
              </label>
              <input
                type="number"
                value={globalSettings.cancellationWindowMinutes}
                onChange={(e) => handleGlobalChange('cancellationWindowMinutes', parseInt(e.target.value) || 0)}
                min={0}
                max={1440}
                className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Délai maximum après la commande pour annuler sans pénalité
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pénalité par défaut (%)
              </label>
              <input
                type="number"
                value={globalSettings.defaultPenaltyPercent}
                onChange={(e) => handleGlobalChange('defaultPenaltyPercent', parseInt(e.target.value) || 0)}
                min={0}
                max={100}
                className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Pourcentage du montant facturé en cas d'annulation
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-700">Motif obligatoire</p>
                <p className="text-xs text-gray-500">Exiger un motif d'annulation</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettings.requireReason}
                  onChange={(e) => handleGlobalChange('requireReason', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-700">Notifier le commerçant</p>
                <p className="text-xs text-gray-500">Envoyer une notification au commerçant</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettings.notifyMerchant}
                  onChange={(e) => handleGlobalChange('notifyMerchant', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-700">Remboursement automatique</p>
                <p className="text-xs text-gray-500">Rembourser automatiquement les annulations</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettings.autoRefund}
                  onChange={(e) => handleGlobalChange('autoRefund', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cancellation Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            Règles d'annulation
          </CardTitle>
          <CardDescription>
            Définissez les différentes règles d'annulation en fonction du délai
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {rules.map((rule) => (
              <div
                key={rule.id}
                className={`p-4 border rounded-lg transition-colors ${
                  rule.isActive
                    ? 'border-gray-200 bg-white'
                    : 'border-gray-100 bg-gray-50 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      rule.isActive ? 'bg-primary-100' : 'bg-gray-200'
                    }`}>
                      <Clock className={`w-4 h-4 ${
                        rule.isActive ? 'text-primary-600' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{rule.name}</h3>
                      <p className="text-sm text-gray-500">{rule.description}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.isActive}
                      onChange={() => handleRuleToggle(rule.id)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                  </label>
                </div>

                {rule.isActive && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-11">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Délai limite (minutes avant le retrait)
                      </label>
                      <input
                        type="number"
                        value={rule.timeLimitMinutes}
                        onChange={(e) => handleRuleChange(rule.id, 'timeLimitMinutes', parseInt(e.target.value) || 0)}
                        min={0}
                        max={1440}
                        className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pénalité (%)
                      </label>
                      <input
                        type="number"
                        value={rule.penaltyPercent}
                        onChange={(e) => handleRuleChange(rule.id, 'penaltyPercent', parseInt(e.target.value) || 0)}
                        min={0}
                        max={100}
                        className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Information Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Les modifications de la politique d'annulation s'appliquent uniquement aux nouvelles commandes.
          Les commandes existantes conservent les règles en vigueur au moment de leur création.
        </AlertDescription>
      </Alert>
    </div>
  )
}

export default CancellationPolicyPage
